/**
 * The console's shell (v109 §3) at /lp-console (built under /lp-console/staging; swapped in 26 Sep 2026, when it
 * took over the old console's banner for preview builds). Global sidebar grouped Core / Operations / Business / Settings, topbar with the page title,
 * the role's notification bell, the signed-in profile, and View As for admins only (the date and time were taken
 * off, Trera 26 Sep 2026).
 *
 * View As is a simulation: it changes what this shell renders and never the stored role (§2, §19). The server keeps
 * authorizing the real user, so a simulated Sales view of an admin still has admin data access; that is what the
 * handoff asks for and it is labelled on screen.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Eye, LogOut, LayoutDashboard, Calendar, Receipt, Users, UserCheck, ClipboardList, MessageSquare, BookOpen, Bell, FileText, Heart, Star, SlidersHorizontal, BarChart3, Shield, ScrollText, type LucideIcon } from 'lucide-react';
import { HOMEPAGE_ASSETS } from '@/lib/homepage/assets';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import StagingBell from './StagingBell';
import { useMessagesUnread } from './useMessagesUnread';
import { Button } from '@/components/admin/ui/button';
import { navFor, pageTitleFor, ROLE_LABELS, STAGING_ROOT } from '@/lib/staging/roleNav';
import { effectiveRole, viewAsOptions, readViewAs, writeViewAs, canViewAs } from '@/lib/staging/viewAs';
import type { UserRole } from '@/types/chat';
import GlobalSearch from './components/GlobalSearch';
import { initials } from '@/lib/staging/initials';

import '@/styles/staging.css';

/** Same icon family as the current console, keyed by the §4 nav entry. */
const NAV_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard, bookings: Calendar, billing: Receipt, clients: Users, talent: UserCheck, coordination: ClipboardList,
  messages: MessageSquare, resources: BookOpen, notifications: Bell, insurance: FileText, referrals: Heart, reviews: Star,
  controls: SlidersHorizontal, reports: BarChart3, users: Shield, audit: ScrollText
};
const NavIcon = ({ k }: { k: string }) => { const I = NAV_ICONS[k]; return I ? <I className="h-4 w-4 shrink-0" aria-hidden="true" /> : <span className="h-4 w-4 shrink-0" />; };

type StagingCtx = { actualRole: UserRole; role: UserRole; viewAs: string | null; setViewAs: (r: string | null) => void };
const Ctx = createContext<StagingCtx | null>(null);
export const useStaging = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStaging must be used inside StagingLayout');
  return v;
};
/** The same context, or null outside StagingLayout (for components that also render standalone). */
export const useStagingOptional = () => useContext(Ctx);

export default function StagingLayout() {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  // Never assume a role: a missing one gets no entries and no View As (it used to fall back to Admin).
  const actualRole = (user?.role || '') as UserRole;
  const storage = typeof window !== 'undefined' ? window.sessionStorage : undefined;
  const [viewAs, setViewAsState] = useState<string | null>(() => readViewAs(storage));
  const setViewAs = (r: string | null) => { setViewAsState(r); writeViewAs(storage, r); };
  const role = effectiveRole(actualRole, viewAs) as UserRole;
  const groups = useMemo(() => navFor(role), [role]);
  const title = pageTitleFor(location.pathname);
  const ctx = useMemo(() => ({ actualRole, role, viewAs, setViewAs }), [actualRole, role, viewAs]);
  const [unread, setUnread] = useState(0);
  const messagesUnread = useMessagesUnread(role);

  // Which deployment this is, from the server. A Render pull-request preview shares production's data, so staff
  // clicking through a PR see a banner saying so before they book, charge or message anyone (carried over from the
  // old console's layout).
  const [appEnv, setAppEnv] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/health').then((r) => (r.ok ? r.json() : null)).then((h) => { if (!cancelled && h?.app_env) setAppEnv(String(h.app_env)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <Ctx.Provider value={ctx}>
      <div className="lp-staging min-h-screen flex w-full">
        <aside className="w-60 shrink-0 border-r flex flex-col" style={{ background: 'var(--sidebar)', borderColor: 'var(--sidebar-border)' }}>
          <div className="px-4 py-4 border-b" style={{ borderColor: 'var(--sidebar-border)' }}>
            <img src={HOMEPAGE_ASSETS.logo} alt="Love &amp; Photos" className="staging-logo" width={160} height={56} />
            <div className="text-xs text-muted-foreground mt-1">Operations console</div>
          </div>
          <nav className="staging-nav flex-1 overflow-y-auto py-2" aria-label="Console">
            {groups.map((g) => (
              <div key={g.group}>
                <div className="staging-group">{g.group}</div>
                {g.items.map((it) => (
                  it.status === 'pending'
                    ? <span key={it.key} className="staging-pending flex items-center gap-3 px-4 py-2 text-sm" title={it.note || ''}><NavIcon k={it.key} />{it.title}<span className="ml-2 text-[10px] uppercase tracking-wide">spec pending</span></span>
                    : <NavLink key={it.key} to={it.path} end={it.key === 'dashboard'} className="flex items-center gap-3 px-4 py-2 text-sm rounded-r-md" title={it.note || undefined}><NavIcon k={it.key} />{it.title}{it.key === 'notifications' && unread > 0 && <span className="ml-auto rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" aria-label={`${unread} unread`}>{unread > 99 ? '99+' : unread}</span>}{it.key === 'messages' && messagesUnread > 0 && <span className="ml-auto rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" aria-label={`${messagesUnread} unread conversations`}>{messagesUnread > 99 ? '99+' : messagesUnread}</span>}</NavLink>
                ))}
              </div>
            ))}
          </nav>
          {role === 'admin' && (
            // Old-console pages with no place in the sidebar yet (26 Sep 2026)
            <div className="px-4 py-3 border-t text-xs text-muted-foreground flex gap-3" style={{ borderColor: 'var(--sidebar-border)' }}>
              <Link to={`${STAGING_ROOT}/sweepstakes`} className="hover:underline">Leads</Link>
              <Link to={`${STAGING_ROOT}/testing`} className="hover:underline">Testing</Link>
            </div>
          )}
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          {appEnv && appEnv !== 'production' && (
            <div role="status" className={`px-6 py-1.5 text-center text-xs font-semibold tracking-wide ${appEnv === 'preview' ? 'bg-amber-500 text-black' : 'bg-sky-600 text-white'}`}>
              {appEnv === 'preview'
                ? 'PREVIEW BUILD · this copy shares the LIVE database and LIVE Stripe. Look, do not book, charge, refund or message.'
                : `${appEnv.toUpperCase()} · not production`}
            </div>
          )}
          {canViewAs(actualRole) && viewAs && viewAs !== actualRole && (
            <div role="status" className="px-6 py-1.5 text-center text-xs font-semibold" style={{ background: '#fff3df', color: '#6b4e12' }}>
              Viewing as {ROLE_LABELS[viewAs] || viewAs}. This is a simulation of that role&apos;s screens; your own role and data access are unchanged.
              <button type="button" className="ml-3 underline" onClick={() => setViewAs(null)}>Back to Admin</button>
            </div>
          )}
          <header className="border-b sticky top-0 z-10" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="px-6 py-3 flex items-center justify-between gap-4">
              <h1 className="text-lg font-semibold truncate">{title}</h1>
              <div className="hidden flex-1 justify-center md:flex"><GlobalSearch role={role} /></div>
              <div className="flex items-center gap-4">
                {canViewAs(actualRole) && (
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>View as</span>
                    <select
                      aria-label="View as role"
                      className="h-8 rounded-md border bg-background px-2 text-sm text-foreground"
                      value={viewAs && viewAs !== actualRole ? viewAs : ''}
                      onChange={(e) => setViewAs(e.target.value || null)}
                    >
                      <option value="">Admin (you)</option>
                      {viewAsOptions(actualRole).map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                    </select>
                  </label>
                )}
                <StagingBell role={role} onCount={setUnread} />
                <Link to={`${STAGING_ROOT}/profile`} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent" title="My Profile">
                  <span aria-hidden="true" className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-muted text-xs font-semibold">{initials(user?.name || user?.email)}</span>
                  <span className="text-right leading-tight">
                    <span className="block text-sm font-medium">{user?.name || user?.email}</span>
                    <span className="block text-xs text-muted-foreground">{ROLE_LABELS[actualRole] || actualRole}</span>
                  </span>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} aria-label="Log out"><LogOut className="h-4 w-4" /></Button>
              </div>
            </div>
          </header>
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </Ctx.Provider>
  );
}

export { STAGING_ROOT };
